import { useEffect, useRef, useState } from "react";

import { hasDataChanged } from "../../utils/hasDataChanged";
import { getFromCache, setToCache } from "../../utils/memoryCache";
import useStrapiList, { type UseStrapiListOptions, type UseStrapiListReturn } from "./useStrapiList";

export interface UseCachedStrapiListOptions<T> extends UseStrapiListOptions {
  cacheKey?: string | null;
  /**
   * Called when fresh data arrives. Return value determines what gets cached.
   * Defaults to identity (caches the items as-is).
   */
  transformForCache?: (items: T[]) => T[];
}

export interface UseCachedStrapiListReturn<T> extends UseStrapiListReturn<T> {
  /**
   * True when we have cached data and are loading fresh data in the background.
   * The UI can use this to show a subtle "refreshing" indicator while keeping stale data visible.
   */
  isRefreshing: boolean;
}

/**
 * Wrapper around useStrapiList that adds memory caching.
 *
 * When a cacheKey is provided:
 * - Immediately returns cached data if available (no loading state)
 * - Fetches fresh data in the background
 * - Updates cache when fresh data differs from cached data
 *
 * This pattern provides instant UI for repeat visits while keeping data fresh.
 */
export function useCachedStrapiList<T>(options: UseCachedStrapiListOptions<T>): UseCachedStrapiListReturn<T> {
  const { cacheKey, transformForCache, ...strapiListOptions } = options;

  // Get cached data immediately (before first render completes)
  const cached = cacheKey ? getFromCache<T[]>(cacheKey) : null;
  const [cachedItems, setCachedItems] = useState<T[]>(cached?.data || []);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const hasInitialCache = useRef(!!cached);
  const mountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const { items: freshItems, isLoading, ...other } = useStrapiList<T>(strapiListOptions);

  // Update cache when fresh data arrives (only if changed)
  useEffect(() => {
    if (!mountedRef.current) return;
    if (!isLoading && freshItems?.length > 0 && cacheKey) {
      const itemsToCache = transformForCache ? transformForCache(freshItems) : freshItems;
      if (hasDataChanged(cachedItems, itemsToCache)) {
        setToCache(cacheKey, itemsToCache);
        setCachedItems(itemsToCache);
      }
      setIsRefreshing(false);
    }
  }, [freshItems, isLoading, cacheKey, cachedItems, transformForCache]);

  // Track refreshing state - when we have cache but are loading fresh data
  useEffect(() => {
    if (!mountedRef.current) return;
    if (isLoading && hasInitialCache.current) {
      setIsRefreshing(true);
    }
  }, [isLoading]);

  // Return cached items if available, otherwise fresh items
  const items = cacheKey && cachedItems.length > 0 ? cachedItems : freshItems;

  // Only show loading state if we don't have cached data
  const effectiveLoading = cacheKey ? !hasInitialCache.current && isLoading : isLoading;

  return {
    items,
    isLoading: effectiveLoading,
    isRefreshing,
    ...other,
  };
}

export default useCachedStrapiList;
